import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import TypewriterAnimation from "@default/general-components/typewriter-animation";
import FieldTime from "@default/general-components/date-time/field-time";
import DateWrapper from "@default/js/participants/components/date-wrapper";
import LocationPinBlack from "@default/assets/ui/location-pin-black.svg?url";
import { Link } from "react-router-dom";
import { AddToCalendar } from "@default/general-components/add-to-calendar";
import { encodeUrl } from "@default/lib/encode-url";
import { FormattedMessage, useIntl } from "react-intl";
import FestivalDay from "@default/general-components/date-time/festival-day";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import SimpleDesignLinkButton from "@default/js/simple-design/simple-design-link-button";
import useTranslate from "@default/hooks/use-translate";
import appearancesInPast from "@default/lib/appearances-in-past";
import IconTime from "@default/general-components/icon-time";
import IconLocation from "@default/general-components/icon-location";
import DateTime from "@default/general-components/date-time/date-time";
import { toMs } from "@default/lib/date-time-helper";

const AppearanceDetailed = ({
  content,
  appearance,
  type,
  showAddToCalendar,
  viewMode,
  appearanceId,
  showEndDate,
}) => {
  const releaseState = useSelector(
    (reduxStore) => reduxStore.appStore.releaseState
  );

  const mainSettings = useSelector(
    (reduxStore) => reduxStore.appStore.mainSettings
  );
  const { simpleDesign } = useAccessibilityContext();
  const _t = useTranslate();

  const isElphi = appearance?.fieldVenueLocation?.label === "Elbphilharmonie";

  return (
    <article className="appearance detailed">
      <div className="container no-padding">
        <div className="row flex-column flex-md-row">
          {(type === "event" || type === "act") && !simpleDesign && (
            <div
              className={`col type body-m make-headline-light ${
                appearanceId === 0 ? "" : "d-none d-lg-flex"
              }`}
            >
              {/* Only show label on first occasion */}
              {appearanceId === 0 && (
                <>
                  {/* All Acts are Concerts */}
                  {type === "act" && (
                    <>{content.fieldParticipantCategory?.name}</>
                  )}

                  {/* Events are having different types */}
                  {type === "event" &&
                    content?.fieldEventType &&
                    content?.fieldEventType?.name !== "Arts & Word" && (
                      <>{content.fieldEventType?.name}</>
                    )}

                  {content?.fieldEventType?.name === "Arts & Word" &&
                    content?.fieldParticipantCategory?.name && (
                      <>{content.fieldParticipantCategory?.name}</>
                    )}
                </>
              )}
            </div>
          )}

          {type === "speaker" && appearance.fieldHappening && (
            <Link
              to={appearance.fieldHappening?.url.path}
              className="col event-name body-m make-headline-light"
            >
              <span>{appearance.fieldHappening.label}</span>

              <SimpleDesignLinkButton
                tabIndex={-1}
                label={`${_t("accessibility.more_about")} ${_t(
                  "accessibility.event"
                )}`}
                size={"small"}
              />
            </Link>
          )}

          <div className="col time-wrapper d-flex flex-row icon-flex-wrap flex-wrap flex-column">
            {(releaseState.showDay || isElphi) &&
              appearance?.fieldDateRawField?.first?.value &&
              !appearance.fieldFake && (
                <>
                  <div className="d-flex body-m gap-sm">
                    <IconTime />
                    <div className="date-time-wrapper d-flex gap-xs">
                      <div className="day make-headline-light">
                        <DateTime
                          value={toMs(appearance.fieldDateRawField.first.value)}
                          mode="weekday_long"
                          granularity={
                            appearancesInPast(appearance) ? "archived" : "date"
                          }
                        />
                      </div>
                      <DateWrapper
                        content={appearance}
                        viewMode={"time"}
                        showEndDate={showEndDate}
                      />
                    </div>
                  </div>
                  <div className="festival-day body-m">
                    {releaseState.showDate && (
                      <FestivalDay
                        appearance={appearance}
                        dependsOnReleaseState={true}
                      />
                    )}
                  </div>
                </>
              )}
            {appearance.fieldFake && (
              <>
                <IconTime />
                <span className="body-m make-headline-light tba">TBA</span>
              </>
            )}
          </div>

          <div className="col venue-wrapper d-flex flex-row icon-flex-wrap gap-sm">
            <>
              <IconLocation />
              {(releaseState.showDate ||
                isElphi ||
                appearancesInPast(appearance)) &&
              appearance.fieldVenueLocation ? (
                <>
                  {appearance.fieldVenueLocation &&
                  viewMode === "detail" &&
                  mainSettings?.fieldVenueOverviewPage ? (
                    <Link
                      to={`${
                        mainSettings?.fieldVenueOverviewPage.uri.path
                      }#${encodeUrl(appearance.fieldVenueLocation.label)}`}
                      className="venue-name body-m action-element"
                    >
                      <TypewriterAnimation>
                        {appearance.fieldVenueLocation.label}
                      </TypewriterAnimation>
                    </Link>
                  ) : (
                    <span className="venue-name body-m">
                      {appearance.fieldVenueLocation.label}
                    </span>
                  )}
                </>
              ) : (
                <span className={"body-m make-headline-light tba"}>TBA</span>
              )}
            </>
          </div>

          {showAddToCalendar &&
            releaseState.showDate &&
            appearance?.fieldPlanned && (
              <div className="col atc d-flex flex-row icon-flex-wrap">
                {showAddToCalendar && (
                  <AddToCalendar content={content} appearance={appearance} />
                )}
              </div>
            )}
        </div>

        {type === "linked-event" && (
          <Link
            to={appearance.fieldHappening?.url.path}
            className="col name d-flex body-m make-headline-light"
          >
            {appearance.fieldHappening?.label}
          </Link>
        )}
      </div>
    </article>
  );
};

AppearanceDetailed.propTypes = {
  content: PropTypes.shape({
    fieldParticipantCategory: PropTypes.shape({
      name: PropTypes.string,
    }),
    fieldEventType: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  appearance: PropTypes.shape({
    fieldVenueLocation: PropTypes.shape({
      label: PropTypes.string,
    }),
    fieldHappening: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.shape({
        path: PropTypes.string,
      }),
    }),
    fieldDateRawField: PropTypes.shape({
      first: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
    fieldFake: PropTypes.bool,
    fieldPlanned: PropTypes.bool,
  }),
  type: PropTypes.string,
  showAddToCalendar: PropTypes.bool,
  viewMode: PropTypes.string,
  appearanceId: PropTypes.number,
  showEndDate: PropTypes.bool,
};

export default AppearanceDetailed;
